/**
 * Remembers that the current browser session was reached by following a magic link.
 *
 * A magic link session holds only the grants written for its token, and its access token is deliberately
 * short lived on a cookie that dies with the browser session. When it expires the backend answers 401 and
 * DataService signs the user out - which for a link recipient means being dropped on a sign-in page they may
 * have no password for. This marker lets the sign-out path say what actually happened instead.
 *
 * Held in sessionStorage rather than in memory because the sign-out may happen after a full page load, and
 * per-tab rather than per-browser because two tabs can legitimately be in different kinds of session.
 */
export class MagicLinkSession {

  private static readonly STORAGE_KEY = 'magicLinkSession';

  /**
   * Names the session a request means. The backend keeps a magic link session in its own cookie, so following a
   * link never replaces an account already signed in on this browser - and it reads that cookie only when a
   * request carries this header. Must match AuthCookie.SESSION_KIND_HEADER in the backend.
   */
  public static readonly SESSION_HEADER = 'X-Ex-Session';
  private static readonly SESSION_HEADER_VALUE = 'link';

  /**
   * The headers marking a request as belonging to this tab's magic link session; empty for an account session.
   */
  public static get requestHeaders(): Record<string, string> {
    return MagicLinkSession.isActive
      ? {[MagicLinkSession.SESSION_HEADER]: MagicLinkSession.SESSION_HEADER_VALUE}
      : {};
  }

  /** Adds the session header to a Headers object if this tab is in a magic link session. */
  public static applyTo(headers: Headers): Headers {
    Object.entries(MagicLinkSession.requestHeaders).forEach(([name, value]) => headers.set(name, value));
    return headers;
  }

  /** Records that a magic link has just been followed successfully. */
  public static markStarted(): void {
    try {
      sessionStorage.setItem(MagicLinkSession.STORAGE_KEY, 'true');
    } catch (ex) {
      // Storage can be unavailable in private browsing modes. Losing the marker only costs a less helpful
      // expiry message, so there is nothing worth reporting here.
    }
  }

  /** True if this tab was opened by following a magic link. */
  public static get isActive(): boolean {
    try {
      return sessionStorage.getItem(MagicLinkSession.STORAGE_KEY) === 'true';
    } catch (ex) {
      return false;
    }
  }

  public static clear(): void {
    try {
      sessionStorage.removeItem(MagicLinkSession.STORAGE_KEY);
    } catch (ex) {
      // Intentionally left blank - see markStarted.
    }
  }
}
